import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  LOCALE_ID,
  output,
} from '@angular/core';
import { DecimalPipe, formatNumber } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { LanguageService } from '../../../core/language.service';

/**
 * Fixed floating service dock. Above the «Позвать официанта» / «Счёт» pill sits an optional
 * cart summary bar (count + total) that shows only when ordering is on and the cart has items;
 * tapping it opens the cart sheet. Every row is a placeholder emit — the parent wires handlers.
 *
 * Primary fill/text come from the AA-resolved --brand-fill / --brand-on-fill host vars; the
 * «Счёт» secondary uses --brand-secondary (brand-tinted onyx). Both stay WCAG AA for any brand.
 */
@Component({
  selector: 'app-action-bar',
  imports: [DecimalPipe, TranslatePipe],
  template: `
    <div
      class="qm-bar fixed inset-x-0 bottom-0 z-40 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-none"
    >
      <div class="mx-auto flex max-w-md flex-col gap-2.5">
        @if (showCart()) {
          <button
            #cartBtn
            type="button"
            (click)="openCart.emit(cartBtn)"
            [attr.aria-label]="cartAria()"
            class="qm-cart-bar pointer-events-auto flex min-h-[3.25rem] items-center gap-3 rounded-[1.5rem] px-4 text-sm font-semibold shadow-[0_10px_28px_-8px_color-mix(in_srgb,var(--brand-primary)_55%,transparent)] transition-transform [text-shadow:0_1px_2px_rgba(0,0,0,0.35)] active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-transparent motion-reduce:transition-none motion-reduce:active:scale-100"
            style="background: var(--qm-accent); color: var(--qm-accent-fg)"
          >
            <span class="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/20">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="8" cy="21" r="1"></circle>
                <circle cx="19" cy="21" r="1"></circle>
                <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
              </svg>
              <span
                class="qm-cart-badge absolute -top-1.5 -right-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-white px-1 text-[11px] font-bold text-slate-900 tabular-nums shadow"
                aria-hidden="true"
                >{{ cartCount() }}</span
              >
            </span>
            <span>{{ 'cart.title' | translate }}</span>
            <span class="ml-auto tabular-nums"
              >{{ cartTotal() | number: '1.0-0' }} {{ 'common.currency' | translate }}</span
            >
          </button>
        }

        @if (showWaiter() || showBill()) {
          <div
            role="group"
            [attr.aria-label]="'header.quickActions' | translate"
            class="qm-dock pointer-events-auto flex items-stretch gap-2.5 rounded-[1.75rem] border border-white/60 bg-white/90 p-2 ring-1 ring-slate-900/10 backdrop-blur-xl shadow-[0_1px_2px_rgba(2,6,23,0.08),0_12px_28px_-8px_rgba(2,6,23,0.24),0_28px_50px_-18px_rgba(2,6,23,0.30),inset_0_1px_0_rgba(255,255,255,0.75)]"
          >
            @if (showWaiter()) {
              <!-- aria-disabled (not disabled) so the cooldown doesn't drop keyboard focus
                   to <body>; TableStore.callWaiter() guards re-entry. -->
              <button
                #waiterBtn
                type="button"
                (click)="callWaiter.emit(waiterBtn)"
                [attr.aria-disabled]="waiterDisabled() ? 'true' : null"
                class="qm-dock-btn qm-dock-btn--waiter flex min-h-[3.25rem] flex-1 min-w-0 items-center justify-center gap-2 rounded-[1.25rem] px-4 text-center text-sm leading-tight font-semibold tracking-tight text-balance [text-shadow:0_1px_2px_rgba(0,0,0,0.35)] shadow-[0_10px_20px_-6px_color-mix(in_srgb,var(--brand-fill,var(--brand-primary))_55%,transparent),0_2px_4px_-1px_color-mix(in_srgb,var(--brand-fill,var(--brand-primary))_45%,transparent)] transition-[transform,box-shadow] duration-200 ease-out active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-transparent aria-disabled:cursor-default aria-disabled:opacity-60 aria-disabled:active:scale-100 motion-reduce:transition-none motion-reduce:active:scale-100"
                style="background: var(--qm-action); color: var(--qm-action-fg)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  focusable="false"
                  class="shrink-0"
                >
                  <path d="M3 20a1 1 0 0 1-1-1v-1a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1Z"></path>
                  <path d="M20 16a8 8 0 1 0-16 0"></path>
                  <path d="M12 4v4"></path>
                  <path d="M10 4h4"></path>
                </svg>
                <span>{{ 'header.callWaiter' | translate }}</span>
              </button>
            }

            @if (showBill()) {
              <button
                #billBtn
                type="button"
                (click)="openBill.emit(billBtn)"
                class="qm-dock-btn qm-dock-btn--bill flex min-h-[3.25rem] flex-1 min-w-0 items-center justify-center gap-2 rounded-[1.25rem] px-4 text-center text-sm leading-tight font-semibold tracking-tight text-balance shadow-[0_6px_14px_-6px_rgba(2,6,23,0.55),inset_0_1px_0_rgba(255,255,255,0.08)] transition-transform duration-200 ease-out active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-transparent motion-reduce:transition-none motion-reduce:active:scale-100"
                style="background: var(--qm-bill); color: var(--qm-bill-fg)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  focusable="false"
                  class="shrink-0"
                >
                  <path
                    d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"
                  ></path>
                  <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path>
                  <path d="M12 17.5v-11"></path>
                </svg>
                <span>{{ 'header.bill' | translate }}</span>
              </button>
            }
          </div>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionBar {
  showWaiter = input(true);
  showBill = input(true);
  showCart = input(false);
  cartCount = input(0);
  cartTotal = input(0);
  /** Disables the waiter button during the post-call cooldown and the pre-call orders check. */
  waiterDisabled = input(false);

  /** Emits the waiter button: with several orders it opens the picker sheet, focus returns here. */
  callWaiter = output<HTMLElement>();
  /** Emits the bill button so the parent can open the orders sheet and restore focus on close. */
  openBill = output<HTMLElement>();
  openCart = output<HTMLElement>();

  private language = inject(LanguageService);
  private locale = inject(LOCALE_ID);

  /** The bar's own text is visible, but the row reads as one control — name it in full. */
  protected readonly cartAria = computed<string>(() =>
    this.language.t('header.cartAria', {
      count: this.cartCount(),
      total: formatNumber(this.cartTotal(), this.locale, '1.0-0'),
      currency: this.language.t('common.currency'),
    }),
  );
}
